package org.mkui.react

import com.macrofocus.common.properties.MutableListProperty
import com.macrofocus.common.properties.MutableProperty
import com.macrofocus.common.properties.PropertyEvent
import com.macrofocus.common.selection.*
import react.StateInstance
import react.StateSetter
import react.useEffect
import react.useState
import kotlin.reflect.KProperty

typealias SetState<T> = (value: T) -> Unit

fun <T> useProperty2(property: MutableProperty<T>): ReactStateInstance<T> {
    val pair : StateInstance<T> = useState { property.value }
    val (_ : T, setStateValue : StateSetter<T>) = pair

    val property = property

    useEffect {
//        println("Registering property $property")
        val listener: (PropertyEvent<T>) -> Unit = { event -> setStateValue(event.newValue) }
        property.addPropertyListener(listener)

        awaitCleanup { property.removePropertyListener(listener) }
    }

    // Memoize ReactStateInstance to avoid re-creating the object unless the components change
//    val reactState = useMemo(pair) {
//        println("Creating ReactStateInstance for property $property")
//        val setPropertyValue : SetState<T> = {
//            this.value = it
//        }
//        ReactStateInstance(pair, setPropertyValue)
//    }
//    return reactState
    return ReactStateInstance(pair, { property.value = it })

}

fun <T> useProperty(property: MutableProperty<T>): ReactStateInstance<T> {
    val pair = useState { property.value }
//    val (getValue, setStateValue) = pair
    val setStateValue: StateSetter<T> = pair.component2()

    useEffect {
//        println("Registering property $property")
        val listener: (PropertyEvent<T>) -> Unit = { event -> setStateValue(event.newValue) }
        property.addPropertyListener(listener)

        awaitCleanup { property.removePropertyListener(listener) }
    }

    // Memoize ReactStateInstance to avoid re-creating the object unless the components change
//    val reactState = useMemo(pair) {
        val setPropertyValue : SetState<T> = { property.value = it }
//        ReactStateInstance(pair, setPropertyValue)
//    }
//    return reactState
    return ReactStateInstance(pair, setPropertyValue)
}

fun <T> useListProperty(property: MutableListProperty<T>): Pair<Array<T>, SetState<Array<T>>> {
    val pair = useState { property.value.toTypedArray()}
    val (getValue, setStateValue) = pair

    val property = property

    val setPropertyValue : SetState<Array<T>> = { property.value = it.toList()}

    useEffect {
        val listener: (PropertyEvent<List<T>>) -> Unit = { event -> setStateValue(event.newValue.toTypedArray()) }
        property.addPropertyListener(listener)
        awaitCleanup { property.removePropertyListener(listener) }
    }
    return Pair(getValue, setPropertyValue)
}

fun <T> useSelection(selection: Selection<T>): Pair<Array<T>, SetState<Array<T>>> {
    val pair = useState { selection.toList().toTypedArray()}
    val (getValue, setStateValue) = pair

    val setPropertyValue : SetState<Array<T>> = {
        throw IllegalStateException("Cannot set the selection of a read-only selection")
    }

    useEffect {
//        val listener = object : SelectionListener<T> {
//            override fun selectionChanged(event: SelectionEvent<T>) {
//                setStateValue(event.model.toList().toTypedArray())
//            }
//        }
        val listener: (SelectionEvent<T>) -> Unit = { event -> setStateValue(event.model.toList().toTypedArray()) }
        selection.addSelectionListener(listener)
        awaitCleanup { selection.removeSelectionListener(listener) }
    }
    return Pair(getValue, setPropertyValue)
}

fun <T> useSelection(selection: MutableSelection<T>): Pair<Array<T>, SetState<Array<T>>> {
    val pair = useState { selection.toList().toTypedArray()}
    val (getValue, setStateValue) = pair

    val setPropertyValue : SetState<Array<T>> = { selection.setSelectedElements(*it)}

    useEffect {
//        val listener = object : SelectionListener<T> {
//            override fun selectionChanged(event: SelectionEvent<T>) {
//                setStateValue(event.model.toList().toTypedArray())
//            }
//        }
        val listener: (SelectionEvent<T>) -> Unit = { event -> setStateValue(event.model.toList().toTypedArray()) }
        selection.addSelectionListener(listener)
        awaitCleanup { selection.removeSelectionListener(listener) }
    }
    return Pair(getValue, setPropertyValue)
}

fun <T> useSingleSelection(selection: SingleSelection<T>): Pair<T?, SetState<T>> {
    val pair = useState { selection.selected }
    val (getValue, setStateValue) = pair

    val setPropertyValue : SetState<T> = {
        throw IllegalStateException("Cannot set the selection of a read-only selection")
    }

    useEffect {
//        val listener = object : SingleSelectionListener<T> {
//            override fun selectionChanged(event: SingleSelectionEvent<T>) {
//                setStateValue(event.model.selected)
//            }
//        }
        val listener: (SingleSelectionEvent<T>) -> Unit = { event -> setStateValue(event.model.selected) }
        selection.addSingleSelectionListener(listener)
        awaitCleanup { selection.removeSingleSelectionListener(listener) }
    }
    return Pair(getValue, setPropertyValue)
}

fun <T> useSingleSelection(selection: MutableSingleSelection<T>): Pair<T?, SetState<T>> {
    val pair = useState { selection.selected }
    val (getValue, setStateValue) = pair

    val setPropertyValue : SetState<T> = { selection.selected = it }

    useEffect {
//        val listener = object : SingleSelectionListener<T> {
//            override fun selectionChanged(event: SingleSelectionEvent<T>) {
//                setStateValue(event.model.selected)
//            }
//        }
        val listener: (SingleSelectionEvent<T>) -> Unit = { event -> setStateValue(event.model.selected) }
        selection.addSingleSelectionListener(listener)
        awaitCleanup { selection.removeSingleSelectionListener(listener) }
    }
    return Pair(getValue, setPropertyValue)
}

class ReactStateInstance<T>(val state: StateInstance<T>, val setState : SetState<T>) {
    inline operator fun component1(): T = state.component1()

    inline operator fun component2(): (T) -> Unit = setState

    inline operator fun getValue(
        thisRef: Nothing?,
        property: KProperty<*>,
    ): T =
        state.component1()

    inline operator fun setValue(
        thisRef: Nothing?,
        property: KProperty<*>,
        value: T,
    ) {
        state.component2().invoke(value)
    }
}
